/*
 * Finding creator records the auto-detection got wrong.
 *
 * The collaborator detection reads names out of video titles and descriptions.
 * It mostly works and occasionally invents a person: a creator called "EMMA"
 * whose only link is instagram.com/vanessastclair is not called Emma, she is
 * Vanessa St. Clair under a character name from the video series.
 *
 * One module so the admin panel and scripts/check-profiles.js cannot drift
 * apart, which is how the creator cards ended up rendering two different ways.
 *
 * Every check reports for a person to judge. None of this is certain enough to
 * act on unattended: merging is not reversible from the UI, and two people
 * genuinely can share an account.
 */

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const SOCIAL_HOSTS =
  /(?:instagram\.com|twitter\.com|x\.com|tiktok\.com|twitch\.tv|youtube\.com)\/@?([A-Za-z0-9._-]+)/i;

const NOT_A_HANDLE = ['channel', 'c', 'user', 'watch', 'playlist'];

/** Every handle readable from a creator's links and channel URL. */
function handlesOf(creator) {
  const out = new Set();
  const urls = [creator.channel, ...((creator.socialLinks || []).map((l) => l.url))].filter(Boolean);
  for (const url of urls) {
    const m = String(url).match(SOCIAL_HOSTS);
    if (m && !NOT_A_HANDLE.includes(m[1].toLowerCase())) out.add(m[1]);
  }
  return [...out];
}

// Words that betray a title fragment rather than a name.
const SENTENCE_WORDS = /\b(my|your|our|his|her|their|the|when|why|how|no|not|with|and|that|this)\b/i;

const looksLikeSentence = (name) => {
  const words = String(name).trim().split(/\s+/);
  return words.length >= 2 && SENTENCE_WORDS.test(name);
};

/*
 * Whether a name is written the way a handle is written.
 *
 * The test is the SHAPE of the string, not whether it matches a handle,
 * because plenty of correct names do: Scott Cramer really is @scottcramer.
 * Matching on content flagged 66 records, most of them right. Matching on
 * shape flags the ones nobody chose: no spaces, and either no capitals at all
 * or containing digits or underscores. That leaves camelCase stage names like
 * NakeyJakey and MrBeast alone, which is how those people are known.
 */
const looksLikeHandle = (name) => {
  const s = String(name).trim();
  if (!s || /\s/.test(s)) return false;
  return s === s.toLowerCase() || /[0-9_]/.test(s);
};

const HANDLE_NOISE = /^(its|it_?s|the|real|official|iam|im|i_?am|x|mr|ms|mrs|thee)/i;

const videoCount = (c) => (c.videoCount != null ? c.videoCount : (c.videos || []).length);

const wordCount = (name) => String(name).trim().split(/\s+/).filter(Boolean).length;

/*
 * How much a name looks like something a person chose, so a merge can suggest
 * which of two names to keep.
 *
 *   0  a handle: "striffleric", "gabbyisbryan"
 *   1  written without care: "claire rice", "EMMA"
 *   2  a name as it would be written: "Claire Belle Rice"
 *
 * Shape only. It cannot tell whether a name is CORRECT, just whether somebody
 * typed it as a name, which is enough to choose between two spellings of one
 * person and is why the result is a suggestion rather than an action.
 */
function nameQuality(name) {
  const s = String(name || '').trim();
  if (!s) return 0;
  if (looksLikeHandle(s)) return 0;
  const letters = s.replace(/[^A-Za-z]/g, '');
  if (!letters) return 1;
  if (letters === letters.toLowerCase()) return 1; // all lower: "claire rice"
  if (letters === letters.toUpperCase()) return 1; // shouted: "EMMA"
  return 2;
}

/*
 * Whether a string the detector pulled out of a title or description is
 * plausibly a person, and therefore worth creating a creator record for.
 *
 * The detector previously accepted anything longer than two characters that
 * was not in a list of twelve common words. Run over 582 unimported videos it
 * proposed 151 new creators, against a database that holds 144 in total, and
 * the proposals included five raw YouTube channel ids, the word "and",
 * "Netflix", "The Plumber", and "NO SUBTITLES", which is a phantom deleted
 * from this database earlier the same day.
 *
 * These rules were written against that list of 151, so they reject the things
 * that were actually wrong rather than the things one imagines might be.
 */

// A YouTube channel id is UC followed by 22 characters. It is never a name,
// and five of them were about to become creators.
const CHANNEL_ID = /^UC[A-Za-z0-9_-]{22}$/;

// Sponsors are the other big source. Descriptions are full of "@simplisafe",
// and a sponsor read is not a collaboration.
const NOT_PEOPLE = new Set(
  [
    'and', 'the', 'with', 'for', 'you', 'your', 'this', 'that', 'new', 'video', 'videos',
    'collab', 'collaboration', 'challenge', 'reaction', 'review', 'episode', 'part', 'shorts',
    'subscribe', 'patreon', 'merch', 'podcast', 'channel', 'live', 'full', 'official',
    'youtube', 'tiktok', 'instagram', 'twitter', 'twitch', 'discord', 'spotify', 'apple',
    'netflix', 'hulu', 'disney', 'amazon', 'google', 'facebook', 'reddit', 'linkedin',
    'shopify', 'squarespace', 'nordvpn', 'audible', 'skillshare', 'hellofresh', 'manscaped',
    'betterhelp', 'raycon', 'honey', 'factor', 'athleticgreens', 'simplisafe', 'bioptimizers',
  ].map((s) => s)
);

// "The Plumber", "The Kids". A definite article in front of a common noun is a
// character in a story, not somebody's name.
const THE_SOMETHING = /^the\s+\w+$/i;

/**
 * @param {string} raw the candidate name
 * @param {Set<string>} existingNames normalised names and handles already held
 */
function isPlausibleCreatorName(raw, existingNames = new Set()) {
  const name = String(raw || '').trim();
  if (name.length < 3 || name.length > 60) return false;
  if (!/[A-Za-z]/.test(name)) return false;
  if (CHANNEL_ID.test(name)) return false;
  if (NOT_PEOPLE.has(name.toLowerCase())) return false;
  if (THE_SOMETHING.test(name)) return false;
  if (looksLikeSentence(name)) return false;

  const key = norm(name);
  if (!key) return false;
  if (existingNames.has(key)) return false;

  // "milesbon1" against an existing "milesbon": the same person with a stray
  // digit, which would otherwise sit alongside them forever.
  const withoutTrailingDigits = key.replace(/\d+$/, '');
  if (withoutTrailingDigits.length >= 3 && existingNames.has(withoutTrailingDigits)) return false;

  return true;
}

/**
 * All findings, most confident first.
 *
 * `creators` need id, name, channel, socialLinks, and either videos or a
 * videoCount. The shape the /api/creators response already has.
 */
function findProfileIssues(creators) {
  const findings = [];
  const byName = new Map();
  for (const c of creators) {
    const k = norm(c.name);
    if (k) byName.set(k, c);
  }

  // Suggest keeping whichever record would cost more to rebuild, then taking
  // the better name from the other. Four of the seven merges done by hand
  // needed exactly that, so it is the rule rather than the exception.
  const suggestMerge = (a, b) => {
    const [keep, drop] = videoCount(a) >= videoCount(b) ? [a, b] : [b, a];
    const better =
      nameQuality(drop.name) > nameQuality(keep.name) ||
      (nameQuality(drop.name) === nameQuality(keep.name) &&
        wordCount(drop.name) > wordCount(keep.name));
    return { type: 'merge', keep, drop, rename: better ? drop.name : null };
  };

  const seenPair = new Set();
  const pairKey = (a, b) => [a.id, b.id].sort((x, y) => x - y).join(':');

  for (const c of creators) {
    for (const h of handlesOf(c)) {
      // 1. the handle IS another creator's name
      const exact = byName.get(norm(h));
      if (exact && exact.id !== c.id && !seenPair.has(pairKey(c, exact))) {
        seenPair.add(pairKey(c, exact));
        findings.push({
          kind: 'handle-is-other-name',
          confidence: 'high',
          creator: c,
          other: exact,
          message: `"${c.name}" links to @${h}, which is ${exact.name}'s name.`,
          suggestion: suggestMerge(c, exact),
        });
        continue;
      }

      // 2. the handle CONTAINS another creator's name
      const bare = norm(h).replace(HANDLE_NOISE, '');
      for (const other of creators) {
        if (other.id === c.id || seenPair.has(pairKey(c, other))) continue;
        const tokens = String(other.name)
          .split(/[^A-Za-z0-9]+/)
          .filter((w) => w.length >= 3)
          .map((w) => w.toLowerCase());
        if (tokens.length < 2) continue;
        const present = tokens.filter((t) => bare.includes(t));
        if (present.length < 2) continue;
        seenPair.add(pairKey(c, other));
        findings.push({
          kind: 'handle-contains-other-name',
          confidence: 'high',
          creator: c,
          other,
          message: `"${c.name}" links to @${h}, which contains "${present.join('" and "')}" from ${other.name}.`,
          suggestion: suggestMerge(c, other),
        });
      }
    }
  }

  // 3. two creators sharing a social URL
  const byUrl = new Map();
  for (const c of creators) {
    for (const l of c.socialLinks || []) {
      const k = String(l.url || '').toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
      if (!k) continue;
      if (!byUrl.has(k)) byUrl.set(k, []);
      byUrl.get(k).push(c);
    }
  }
  for (const [url, group] of byUrl) {
    if (group.length < 2) continue;
    for (let i = 1; i < group.length; i += 1) {
      const [a, b] = [group[0], group[i]];
      if (seenPair.has(pairKey(a, b))) continue;
      seenPair.add(pairKey(a, b));
      findings.push({
        kind: 'shared-link',
        // Lower, because a shared account is an ordinary thing. Two names on
        // one Instagram may well be two people.
        confidence: 'medium',
        creator: a,
        other: b,
        message: `${a.name} and ${b.name} both link to ${url}.`,
        suggestion: suggestMerge(a, b),
      });
    }
  }

  // 4. not a person at all
  for (const c of creators) {
    if (!looksLikeSentence(c.name)) continue;
    findings.push({
      kind: 'not-a-person',
      confidence: 'high',
      creator: c,
      other: null,
      message: `"${c.name}" reads as a fragment of a video title, not a name.`,
      suggestion: { type: 'delete', creator: c },
    });
  }

  // 5. named after the handle
  for (const c of creators) {
    if (!looksLikeHandle(c.name)) continue;
    const n = norm(c.name);
    const matches = handlesOf(c).some(
      (h) => norm(h) === n || norm(h).replace(HANDLE_NOISE, '') === n
    );
    if (!matches) continue;
    findings.push({
      kind: 'named-after-handle',
      confidence: 'medium',
      creator: c,
      other: null,
      message: `"${c.name}" is their handle rather than a name anyone chose.`,
      suggestion: { type: 'rename', creator: c },
    });
  }

  const order = { high: 0, medium: 1, low: 2 };
  return findings.sort(
    (a, b) =>
      order[a.confidence] - order[b.confidence] ||
      videoCount(b.creator) - videoCount(a.creator)
  );
}

const ISSUE_LABELS = {
  'handle-is-other-name': 'Same person, different name',
  'handle-contains-other-name': 'Same person, different name',
  'shared-link': 'Shared social link',
  'not-a-person': 'Not a person',
  'named-after-handle': 'Named after their handle',
};

/*
 * CommonJS on purpose. webpack imports this happily from App.js, and
 * scripts/check-profiles.js can require the very same file, so the admin panel
 * and the command line cannot drift apart. That drift is exactly how the
 * creator cards ended up rendering two different ways.
 */
module.exports = {
  norm,
  handlesOf,
  looksLikeSentence,
  looksLikeHandle,
  nameQuality,
  findProfileIssues,
  isPlausibleCreatorName,
  ISSUE_LABELS,
};
